import { VChip } from "vuetify/components";
import UserIcon from "~/components/Atoms/UserIcon.vue";
import type { ContractMember } from "~/entities/ContractMember";
import { tagTypeUniqeId, type UserTagType } from "~/entities/UserTagType";

type openReviewModalType = (planId: string, userId: string) => void;

export const tableHeaders = (
  isSp: boolean,
  dataVolume: "planBase" | "allData",
  tagTypes: UserTagType[],
  openReviewModal: openReviewModalType,
) => {
  const headers = [];
  const tagTypeUniqueKeys = tagTypes.map(tagType => tagTypeUniqeId(tagType));
  // sp row 1
  headers.push({
    label: "",
    key: "name",
    sortable: false,
    align: "left",
    width: 25,
    spAlign: "left",
    spWidth: 100,
    clickable: true,
    render: (item: ContractMember) => {
      const firstData: VNode[] = [];
      const lastData: VNode[] = [];
      return h("div", { class: `mr-auto align-center ${isSp ? "d-flex ga-2" : ""}` }, [
        ...firstData,
        h("div", { class: `d-flex align-center` }, [
          h("div", { style: "width: 30px" }, [
            h(UserIcon, {
              src: item.clientUser.userImage,
              width: 30,
              height: 30,
            }),
          ]),
          h("div", { class: "mx-2" }, [
            h("div", { class: "text-left" }, omitString(item.clientUser.fullName, 16)),
            h("div", { class: "text-left" }, item.clientUser.userName),
          ]),
        ]),
        ...lastData,
      ]);
    },
  });

  // when dataVolume is 'allData'
  if (dataVolume === "allData") {
    headers.push({
      label: "講座タグ",
      key: "plan_tag",
      filterable: true,
      align: "center",
      width: 30,
      spAlign: "center",
      spWidth: 100,
      clickable: true,
      render: (item: ContractMember) => {
        return h("div", { class: `d-flex justify-center flex-wrap ga-1 ${isSp ? "flex-column" : "flex-row"}` }, [
          item.userTags.map(tag => {
            const key = `${tag.userTagTypeId}` + `${tag.tagKey1}` + `${tag.tagKey2}` + `${tag.tagKey3}`;
            return h(
              "div",
              h(
                VChip,
                {
                  rounded: "xl",
                  size: "small",
                  variant: "outlined",
                  color: tagTypeUniqueKeys.includes(key) ? "primary" : "white",
                },
                {
                  default: () => omitString(tag.userTagName, 15),
                },
              ),
            );
          }),
        ]);
      },
    });

    headers.push({
      label: "契約変更日",
      key: "contract_day",
      sortable: true,
      align: "center",
      width: 15,
      spAlign: "center",
      spWidth: 100 / 2,
      clickable: true,
      render: (item: ContractMember) => {
        if (item.endDate) {
          return FormatDateString(item.endDate, "yyyy年MM月dd日");
        }
        return FormatDateString(item.startDate, "yyyy年MM月dd日");
      },
    });
  }

  if (dataVolume === "planBase") {
    // sp row 2
    headers.push({
      label: "プランタグ",
      key: "price_tag",
      filterable: true,
      align: "center",
      width: 30,
      spAlign: "center",
      spWidth: 100,
      clickable: true,
      render: (item: ContractMember) => {
        return h("div", { class: `d-flex justify-center flex-wrap ga-1 ${isSp ? "flex-column" : "flex-row"}` }, [
          item.userTags.map(tag => {
            const key = `${tag.userTagTypeId}` + `${tag.tagKey1}` + `${tag.tagKey2}` + `${tag.tagKey3}`;
            return h(
              VChip,
              {
                rounded: "xl",
                size: "small",
                variant: "outlined",
                color: tagTypeUniqueKeys.includes(key) ? "primary" : "white",
              },
              {
                default: () => omitString(tag.userTagName, 15),
              },
            );
          }),
        ]);
      },
    });

    headers.push({
      label: isSp ? "契約開始日/終了日" : "契約開始日\n契約終了日",
      key: "contract_day",
      sortable: true,
      align: "center",
      width: 15,
      spAlign: "center",
      spWidth: 100 / 3,
      clickable: true,
      render: (item: ContractMember) => {
        // @todo 契約変更日を使う
        return [
          h("div", { class: "text-center" }, FormatDateString(item.startDate, "yyyy年MM月dd日") || "-"),
          h("div", { class: "text-center" }, FormatDateString(item.endDate, "yyyy年MM月dd日") || "-"),
        ];
      },
    });

    // sp row 2
    headers.push({
      label: "契約日数",
      key: "contract_days",
      sortable: true,
      align: "center",
      width: 15,
      spAlign: "center",
      spWidth: 100 / 3,
      clickable: true,
      render: (item: ContractMember) => {
        return item.contractDays || "-";
      },
    });
  }

  // sp row 2
  headers.push({
    label: "投稿数",
    key: "posts_count",
    sortable: true,
    align: "center",
    width: 15,
    spAlign: "center",
    spWidth: 100 / 3,
    clickable: true,
    render: (item: ContractMember) => {
      return numberFormat(item.postsCount) || "-";
    },
  });

  // 2.7.0のリリースで除く
  // sp row 2
  // headers.push({
  //   label: "視聴回数",
  //   key: "view_count",
  //   sortable: true,
  //   align: "center",
  //   width: 10,
  //   spAlign: "center",
  //   spWidth: 100 / 3,
  //   clickable: true,
  //   render: (item: ContractMember) => {
  //     return numberFormat(item.viewCount) || "-";
  //   },
  // });

  // 2.7.0のリリースで除く
  // sp row 2
  // headers.push({
  //   label: "訪問回数",
  //   key: "visit_count",
  //   sortable: true,
  //   align: "center",
  //   width: 10,
  //   spAlign: "center",
  //   spWidth: 100 / 3,
  //   clickable: true,
  //   render: (item: ContractMember) => {
  //     return numberFormat(item.visitCount) || "-";
  //   },
  // });

  if (dataVolume === "planBase") {
    // sp row 3
    headers.push({
      label: "体験談",
      key: "review_point",
      sortable: true,
      align: "center",
      width: 10,
      spAlign: "center",
      spWidth: 100 / 2,
      clickable: false,
      render: (item: ContractMember) => {
        if (item.reviewId) {
          return h(
            "div",
            {
              class: "text-value cursor-pointer",
              onClick: () => openReviewModal(item.planId, item.clientUser.userId),
            },
            [
              h(
                "span",
                { class: item.isPrivateReview ? "text-main-04" : "text-main-01" },
                item.isPrivateReview ? "非公開" : "公開",
              ),
            ],
          );
        } else {
          return "-";
        }
      },
    });
  }

  // sp row 3
  headers.push({
    label: "ログイン",
    key: "last_login_at",
    sortable: true,
    align: "center",
    width: 15,
    spAlign: "center",
    spWidth: 100 / 2,
    clickable: true,
    render: (item: ContractMember) => {
      const items = [];
      if (item.lastLogin.includes("ログイン中")) {
        items.push(h("div", { class: "text-blue text-center text-caption" }, "✔"));
      }
      items.push(h("div", { class: "text-center" }, item.lastLogin || "-"));
      return h("div", { class: "text-center" }, items);
    },
  });

  return headers;
};

export const spRows = () => {
  return [
    [{ key: "name" }],
    [{ key: "contract_day" }, { key: "contract_days" }, { key: "posts_count" }],
    [{ key: "plan_tag" }, { key: "price_tag" }],
    [{ key: "review_point" }, { key: "last_login_at" }],
  ];
};
